import { HttpClient } from '@angular/common/http'
import { Injectable, computed, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable } from 'rxjs'
import { tap } from 'rxjs/operators'

import { NotificationService } from './notification.service'

/** Fallback when API does not send idleTimeoutMs (e.g. older server). Conservative default to avoid premature logouts. */
const DEFAULT_IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private httpClient = inject(HttpClient)
    private router = inject(Router)
    private notification = inject(NotificationService)

    private idleTimerId: ReturnType<typeof setTimeout> | null = null
    /** From API CompleteLogin.IdleTimeoutMs; matches server cookie ExpireTimeSpan. */
    private idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS
    /** Prevents duplicate toasts and redirects when multiple sources detect session expiry at once. */
    private isRedirectingToSignIn = false

    userId = signal('')
    displayName = signal('')
    environmentName = signal('')
    ownedFeatures = signal([] as string[])
    requestedRoute!: string
    username = signal('')
    hrref!: number
    positionCode = signal('')
    isInitialized = signal(false)
    private _isInitialized$ = new BehaviorSubject<boolean>(false)

    firstName = computed(() => {
        if (!this.displayName()) return ''
        if (this.isLessor()) return this.displayName()
        if (this.displayName()) return this.displayName().split(' ')[0]
        if (this.userId()) return this.userId()
        return ''
    })

    isLessor = computed(() => {
        if (this.username() && this.username().toLowerCase()[0] === 'v' && this.username().toLowerCase()[1] !== 'c') {
            return true
        }
        return false
    })

    isLoggedIn$ = this.httpClient.get('api/Account/IsLoggedIn') as Observable<boolean>

    get isInitialized$(): Observable<boolean> {
        return this._isInitialized$.asObservable()
    }

    public hasFeature(features: string[]): boolean {
        if (Array.isArray(features)) {
            // if we have an array of features, we only need to match one of them (OR) to be true.
            // we don't yet need to support matching all of them (AND).
            for (const feature of features) {
                if (this.ownedFeatures().contains(feature)) return true
            }
        } else {
            if (this.ownedFeatures().contains(features)) return true
        }
        return false
    }

    completeLogin(): Observable<any> {
        return this.httpClient.get('api/Account/CompleteLogin').pipe(
            tap(result => {
                const initData = result as any
                if (initData.succeeded) {
                    this.ownedFeatures.set(initData.rights)
                    this.displayName.set(initData.displayName)
                    this.userId.set(initData.userId)
                    this.environmentName.set(initData.environmentName)
                    this.username.set(initData.userName)
                    this.hrref = initData.hrref
                    this.positionCode.set(initData.positionCode ?? '')
                    this.idleTimeoutMs =
                        typeof initData.idleTimeoutMs === 'number' && initData.idleTimeoutMs > 0
                            ? initData.idleTimeoutMs
                            : DEFAULT_IDLE_TIMEOUT_MS
                    this.startIdleTimer()
                }
                // Mark authentication as initialized regardless of success/failure
                this.isInitialized.set(true)
                this._isInitialized$.next(true)
            }),
        )
    }

    logout() {
        this.clearAuthState()
        this.httpClient.get('api/Account/logout').subscribe({
            next: () => this.router.navigate(['signin']),
            error: error => console.error(error),
        })
    }

    /** Clears auth state and navigates to sign-in. Use when session expired (e.g. 401) so sign-in page can show a message. */
    clearAuthAndNavigateToSignIn(sessionExpired: boolean) {
        if (this.isRedirectingToSignIn) return
        this.isRedirectingToSignIn = true
        this.clearAuthState()
        this.router.navigate(['/signin'], sessionExpired ? { queryParams: { sessionExpired: 'true' } } : {})
        setTimeout(() => {
            this.isRedirectingToSignIn = false
        }, 2000)
    }

    /** Client-side idle timer: redirect to sign-in when idle for idleTimeoutMs (from API) without any API call. No API call at expiry, so cookie is not refreshed. */
    startIdleTimer(): void {
        this.clearIdleTimer()
        this.idleTimerId = setTimeout(() => {
            this.idleTimerId = null
            this.clearAuthAndNavigateToSignIn(true)
        }, this.idleTimeoutMs)
    }

    /** Reset idle timer on API activity (each response that would have extended the cookie). */
    resetIdleTimer(): void {
        if (this.userId() && this.username()) {
            this.startIdleTimer()
        }
    }

    private clearAuthState(): void {
        this.clearIdleTimer()
        this.ownedFeatures.set([])
        this.userId.set('')
        this.displayName.set('')
        this.username.set('')
        this.positionCode.set('')
        this.isInitialized.set(false)
        this._isInitialized$.next(false)
    }

    private clearIdleTimer(): void {
        if (this.idleTimerId != null) {
            clearTimeout(this.idleTimerId)
            this.idleTimerId = null
        }
    }
}
